import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { routes } from '../../core.index';
import { CommonService } from './../../service/common/common.service';
import { CommonModule } from '@angular/common';
import { HeaderV2Component } from '../header-v2/ui/header-v2.component';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-landing',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule,HeaderV2Component],
    templateUrl: './landing-page.component.html'
})
export class LandingPageComponent implements OnInit {
    private router = inject(Router);
    public commonService = inject(CommonService);
    public routes = routes;
    currentDate = new Date();
    userProfile: any = null;
    user: any = null;
    searchQuery = '';
    
    get availableApps() {
        const apps = [
            {
                id: 'vms',
                name: 'VMS',
                description: 'Visitor Management System',
                theme: 'orange',
                link: () => this.toVms(),
                hasAccess: this.canAccessVMS()
            },
            {
                id: 'admin',
                name: 'Admin',
                description: 'User access & settings',
                theme: 'blue',
                link: () => this.toAdmin(),
                hasAccess: this.canAccessAdmin()
            },
            {
                id: 'grievance',
                name: 'Grievance',
                description: 'Raise & track concerns',
                theme: 'orange',
                link: () => this.toGrievance(),
                hasAccess: this.isAdminLogin() || this.isGrievanceModuleAccess() || this.isRaiseGrievanceAccess()
            },
            {
                id: 'expense',
                name: 'Expense',
                description: 'Claims & reimbursements',
                theme: 'blue',
                link: () => this.toExpense(),
                hasAccess: this.allowExpenseModule
            },
            {
                id: 'conference',
                name: 'Conference Room',
                description: 'Manage conferences and meetings',
                theme: 'blue',
                link: () => this.toConferenceRoom(),
                hasAccess: this.canAccessConferenceRoom()
            },
            {
                id: 'ai-buddy',
                name: 'AI Buddy',
                description: 'Your AI assistant for work',
                theme: 'green',
                link: () => this.toAiBuddy(),
                hasAccess: environment.clo_ai_buddy
            },
            {
                id: 'approval-notes',
                name: 'Approval Notes',
                description: 'Review and approve notes',
                theme: 'blue',
                link: () => this.toApprovalNotes(),
                hasAccess: this.canAccessApprovalNotes()
            },
            {
                id: 'documents',
                name: 'Documents',
                description: 'Upload, organize and share files',
                theme: 'blue',
                link: () => this.toDocuments(),
                hasAccess: this.commonService.canAccessModule('dms')
            }
        ];

        return apps.filter(app => app.hasAccess);
    }

    get filteredApps() {
        const isAdmin = this.commonService.isAdmin();
        let apps = this.availableApps.filter(app => {
            if (app.id === 'ai-buddy' && !isAdmin) {
                return false;
            }
            return true;
        });
        if (!this.searchQuery) return apps;
        const query = this.searchQuery.trim().toLowerCase();
        return apps.filter(app =>
            app.name.toLowerCase().includes(query) ||
            app.description.toLowerCase().includes(query)
        );
    }

    allowExpenseModule = false;

    ngOnInit(): void {
        const profile = localStorage.getItem('userProfile');
        if (!profile) {
            this.router.navigate(['/']);
            return;
        }
        this.userProfile = JSON.parse(profile);
        this.allowExpenseModule =this.commonService.isPlantAllowed();
    }

    get welcomeName(): string {
        const profile = this.userProfile;
        const firstName = profile?.profile?.firstname || profile?.firstname || '';
        const lastName = profile?.profile?.lastname || profile?.lastname || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        return fullName || profile?.profile?.email || profile?.username || 'Guest';
    }

    private getFirstAccessibleRoute(module: string): string {
        const moduleRoutes: { [key: string]: { permission: string; route: string }[] } = {
            'vms': [
                { permission: 'vms-dashboard', route: '/vms/dashboard' },
                { permission: 'vms-frontdesk', route: '/vms/frontdesk' },
                { permission: 'vms-invitation', route: '/vms/invitations' },
                { permission: 'vms-visitors', route: '/vms/visitors' },
                { permission: 'raise-grievance-access', route: '/grievance/employee/portal' }
            ],
            'admin': [
                { permission: 'admin-users', route: '/admin/users' },
                { permission: 'admin-roles', route: '/admin/roles-permissions' },
                { permission: 'admin-orgunit', route: '/admin/organizations' },
                { permission: 'admin-desk', route: '/admin/desks' }
            ],
            'account': [
                { permission: 'account-myaccount', route: '/account/my-account' }
            ],
            'grievance': [
                { permission: 'grievance-approval', route: '/grievance/dashboard' },
                { permission: 'grievance-approval', route: '/grievance/request' },
                { permission: 'grievance-settings', route: '/grievance/settings' },
            ],
            'expense': [
                { permission: '', route: '/expense/dashboard' },
                { permission: 'expense-my-trip', route: '/expense/my-trip' },
                { permission: 'expense-approvals', route: '/expense/trip-approvals' },
                { permission: 'expense-settings', route: '/expense/settings/expense-types' },
                { permission: 'expense-settings', route: '/expense/settings/expense-approval-matrix' },
                { permission: 'expense-settings', route: '/expense/settings/gl-master' },
                { permission: 'expense-settings', route: '/expense/settings/cost-center' },
                { permission: 'expense-settings', route: '/expense/settings/travel-policy' },
                { permission: 'expense-settings', route: '/expense/settings/project-base' },
                { permission: 'expense-settings', route: '/expense/settings/travel-desk-master' },
                { permission: 'expense-reimbursement', route: '/expense/my-reimbursement' },
                { permission: 'expense-reimbursement-approval', route: '/expense/reimbursement-approval' },
                { permission: 'expense-travel-desk', route: '/expense/travel-desk' },
                { permission: 'expense-dashboard', route: '/expense/dashboard' },
                { permission: 'expense-trip-history', route: '/expense/reports/trip-history' },
                { permission: 'expense-trip-roster', route: '/expense/reports/trip-roster' },
                { permission: 'expense-claimsummary', route: '/expense/reports/claim-summary' },
            ],
            'conference-room': [
                { permission: '', route: '/conference-room/dashboard' },
                { permission: '', route: '/conference-room/my-bookings' },
                { permission: 'cr-room-display', route: '/conference-room/room-display' },
                { permission: 'cr-approvals', route: '/conference-room/approvals' },
                { permission: 'cr-reports', route: '/conference-room/reports/room-utilisation' },
                { permission: 'cr-reports', route: '/conference-room/reports/approval-tracking' },
                { permission: 'cr-reports', route: '/conference-room/reports/booking-summary' },
                { permission: 'cr-amenities', route: '/conference-room/settings/amenities' },
                { permission: 'cr-conference-room-master', route: '/conference-room/settings/conference-rooms' },
            ],
            'ai-buddy': [
                { permission: '', route: '/ai-buddy/conversations' },
                { permission: '', route: '/ai-buddy/alerts' },
                { permission: '', route: '/ai-buddy/employees' }
            ],
            'approval-notes': [
                { permission: '', route: '/approval-notes/dashboard' },
                { permission: 'approval-notes-budget-approval-display', route: '/approval-notes/budget-approval' },
                { permission: 'approval-notes-vendor-ticket-display', route: '/approval-notes/vendor-ticket' },
                { permission: 'approval-notes-vendor-selection-display', route: '/approval-notes/vendor-selection' },
                { permission: 'approval-notes-scrap-approval-display', route: '/approval-notes/scrap-approval' },
                { permission: 'approval-notes-vendor-creation-display', route: '/approval-notes/vendor-creation' },
                { permission: 'approval-notes-payment-initiate-display', route: '/approval-notes/payment-approval' },
                { permission: 'approval-notes-vendors-display', route: '/approval-notes/vendors' },
            ],
        };

        const permissions = this.commonService.getUserPermissions();
        const routes = moduleRoutes[module];

        if (module === 'expense') return '/expense/dashboard';
        if (module === 'conference-room') return '/conference-room/dashboard';
        if (module === 'ai-buddy') return '/ai-buddy/conversations';
        if (module === 'approval-notes') return '/approval-notes/dashboard';

        if (!routes) return '/';

        if (this.commonService.isAdmin()) {
            return routes.length > 0 ? routes[0].route : '/';
        }

        const firstAccessibleRoute = routes.find(route =>
            permissions.includes(route.permission)
        );

        return firstAccessibleRoute ? firstAccessibleRoute.route : '/';
    }

    toAccount() {
        this.router.navigate([routes.account]);
    }

    // Check if user has access to any VMS routes
    canAccessVMS(): boolean {
        return this.commonService.canAccessModule('vms');
    }

    // Check if user has access to any Admin routes
    canAccessAdmin(): boolean {
        return this.commonService.canAccessModule('admin');
    }

    canAccessAccount(): boolean {
        return this.commonService.canAccessModule('account');
    }

    canAccessGrievance(): boolean {
        return this.commonService.canAccessModule('grievance');
    }

    toDocuments() {
        this.router.navigate(['/dms']);
    }

    canAccessExpense(): boolean {
        return this.commonService.canAccessModule('expense');
    }

    canAccessConferenceRoom(): boolean{
        return this.commonService.canAccessModule('conference-room');
    }

    // Updated navigation methods
    toVms() {
        const route = this.getFirstAccessibleRoute('vms');
        this.router.navigate([route]);
    }

    toAdmin() {
        const route = this.getFirstAccessibleRoute('admin');
        this.router.navigate([route]);
    }

    toGrievance() {
        // Check if self mode is enabled
        if (localStorage.getItem('self') === 'true') {
            const userProfileStr = localStorage.getItem('userProfile');
            if (!userProfileStr) {
                this.router.navigate(['/']);
                return;
            }
            const userProfile = JSON.parse(userProfileStr);
            const orgUnits = userProfile?.org_units || [];
            if (orgUnits.length > 0) {
                this.router.navigate(['/grievance/employee/portal']);
            } else {
                this.router.navigate(['/apps']);
            }
            return;
        }

        // Check if user has grievance module access
        const userProfileStr = localStorage.getItem('userProfile');
        if (!userProfileStr) {
            this.router.navigate(['/']);
            return;
        }

        const userProfile = JSON.parse(userProfileStr);
        const moduleNames = userProfile?.roles?.module_names || [];

        if (!moduleNames.includes('grievance')) {
            // No module access, redirect to employee portal
            const orgUnits = userProfile?.org_units || [];
            if (orgUnits.length > 0) {
                this.router.navigate(['/grievance/employee/portal']);
            } else {
                this.router.navigate(['/apps']);
            }
            return;
        }

        // User has module access, check if they have any permissions
        const permissions = this.commonService.getUserPermissions();
        const hasGrievancePermission = permissions.some(p =>
            p === 'grievance-approval' || p === 'grievance-settings'
        );

        if (hasGrievancePermission) {
            // User has permissions, route to first accessible page
            const route = this.getFirstAccessibleRoute('grievance');
            this.router.navigate([route]);
        } else {
            // User has module but no permissions, show no-permission page
            this.router.navigate(['/grievance/no-permission']);
        }
    }

    toExpense() {
        const route = this.getFirstAccessibleRoute('expense');
        this.router.navigate([route]);
    }

    toConferenceRoom() {
        const route = this.getFirstAccessibleRoute('conference-room');
        this.router.navigate([route]);
    }
    
    toAiBuddy() {
        const route = this.getFirstAccessibleRoute('ai-buddy');
        this.router.navigate([route]);
    }

    toApprovalNotes() {
        const route = this.getFirstAccessibleRoute('approval-notes');
        this.router.navigate([route || '/approval-notes/dashboard']);
    }

    canAccessApprovalNotes(): boolean {
        return this.commonService.canAccessApprovalNotes();
    }

    getAccessibleModulesCount(): number {
        const modules = [
            this.canAccessAccount(),
            this.canAccessVMS(),
            this.canAccessAdmin(),
            this.canAccessGrievance(),
            this.canAccessExpense(),
            this.canAccessConferenceRoom(),
            this.canAccessApprovalNotes(),
            this.commonService.canAccessModule('dms')
        ];
        if (this.commonService.isAdmin()) {
            return modules.length
        }
        return modules.filter(Boolean).length;
    }

    toLogout() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('selectedOrgUnit');
        localStorage.removeItem('userId');
        localStorage.removeItem('selectedDesk');
        localStorage.removeItem('userProfile');
        localStorage.removeItem('orgUnits');
        // localStorage.clear();
        // this.router.navigate([routes.signIn]);
        window.location.href = '/auth/signin';
    }

    goToModule(route: string): void {
        this.router.navigate([route]);
    }

    isAdminLogin(): boolean {
        return this.commonService.isAdmin();
    }

    isGrievanceModuleAccess(): boolean {
        const userProfileStr = localStorage.getItem('userProfile');
        if (!userProfileStr) return false;
        
        const userProfile = JSON.parse(userProfileStr);
        const moduleNames = userProfile?.roles?.module_names || [];
        return moduleNames.includes('grievance');
    }

    toAms() {
        this.router.navigate(['ams']);
    }

    isRaiseGrievanceAccess(): boolean {
        const permissions = this.commonService.getUserPermissions();
        return permissions.includes('raise-grievance-access');
    }


}
